import { useId, useRef, useState } from "react";
import { Avatar, Icon } from "../../components";
import type { ClueEvidence } from "../impostor-evidence";
type Selection = {
  eligibleIds: readonly string[];
  selectedId: string | null;
  disabled: boolean;
  onSelect: (id: string) => void;
};
export function ImpostorEvidence({
  cards,
  actorId,
  viewerId,
  selection,
  offlineIds,
  ballot = false,
  votes,
}: {
  cards: readonly ClueEvidence[];
  actorId: string | null;
  viewerId?: string | null;
  selection?: Selection;
  /** Public connection state from the room roster. */
  offlineIds?: readonly string[];
  /** Vote phases: clues wrap so they can be read without expanding. */
  ballot?: boolean;
  /** Completed public ballots per target (guess phase only). */
  votes?: Readonly<Record<string, number>>;
}) {
  const group = useId(),
    [expanded, setExpanded] = useState<string | null>(null),
    trigger = useRef<HTMLButtonElement | null>(null);
  const detail = cards.find((c) => c.playerId === expanded);
  const close = () => {
    setExpanded(null);
    requestAnimationFrame(
      () => trigger.current?.isConnected && trigger.current.focus(),
    );
  };
  return (
    <section
      className={`evidence-region${cards.length <= 3 ? " is-sparse" : ""}${cards.length > 6 ? " is-dense" : ""}${cards.length > 9 ? " is-crowded" : ""}${selection || ballot ? " is-ballot" : ""}`}
      aria-label="Public clues"
      data-tour="evidence"
    >
      <div
        className="evidence-grid"
        inert={Boolean(detail)}
        role={selection ? "radiogroup" : undefined}
        aria-label={selection ? "Choose who to accuse" : undefined}
      >
        {cards.map((c) => {
          const eligible = selection?.eligibleIds.includes(c.playerId);
          const selected = eligible && selection?.selectedId === c.playerId;
          const offline = offlineIds?.includes(c.playerId) ?? false;
          return (
            <article
              className={`evidence-card${c.text ? " has-clue" : " is-pending"}${selected ? " is-selected" : ""}${actorId === c.playerId ? " is-actor" : ""}${offline ? " is-offline" : ""}`}
              key={c.playerId}
              data-tour={actorId === c.playerId ? "turn-card" : undefined}
            >
              <label
                className="evidence-choice"
                htmlFor={eligible ? `${group}-${c.playerId}` : undefined}
              >
                <span className="evidence-name">
                  <span className="seat-avatar">
                    <Avatar name={c.name} index={c.seat - 1} small />
                    {offline ? (
                      <i className="seat-offline-dot" aria-hidden="true" />
                    ) : null}
                  </span>
                  <strong>
                    {c.name}
                    {offline ? <span className="sr-only"> (offline)</span> : null}
                  </strong>
                </span>
                {c.text ? (
                  <span
                    className={`evidence-clue${c.text.length > 32 ? " is-long" : ""}`}
                    key={c.text}
                  >
                    {c.text}
                  </span>
                ) : (
                  <span className="evidence-waiting">
                    {actorId === c.playerId ? (
                      <>
                        <i aria-hidden="true" />
                        {viewerId === c.playerId
                          ? "Your turn"
                          : offline
                            ? "Offline"
                            : "Their turn"}
                      </>
                    ) : (
                      "Waiting"
                    )}
                  </span>
                )}
                {votes?.[c.playerId] ? (
                  <span className="evidence-votes">
                    {votes[c.playerId]}{" "}
                    {votes[c.playerId] === 1 ? "vote" : "votes"}
                  </span>
                ) : null}
                {eligible ? (
                  <input
                    id={`${group}-${c.playerId}`}
                    type="radio"
                    name={group}
                    aria-label={`Accuse ${c.name}`}
                    checked={Boolean(selected)}
                    disabled={selection?.disabled}
                    onChange={() => selection?.onSelect(c.playerId)}
                  />
                ) : null}
              </label>
              {c.text ? (
                <button
                  type="button"
                  className="evidence-expand"
                  aria-label={`Read clue from ${c.name}`}
                  onClick={(e) => {
                    trigger.current = e.currentTarget;
                    setExpanded(c.playerId);
                  }}
                >
                  <Icon name="arrow" size={16} />
                </button>
              ) : null}
            </article>
          );
        })}
      </div>
      {detail ? (
        <section
          className="evidence-detail"
          role="region"
          aria-label="Full clue"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              close();
            }
          }}
        >
          <div>
            <strong>{detail.name}</strong>
            <button
              type="button"
              className="secondary"
              aria-label="Close clue"
              onClick={close}
              autoFocus
            >
              Close
            </button>
          </div>
          <p>{detail.text}</p>
        </section>
      ) : null}
    </section>
  );
}
